import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  Tabs,
  ErrorBanner,
  formatDate,
  type BadgeColor,
} from '../components/ui';

type Tab = 'campaigns' | 'segments' | 'sequences' | 'subscribers';

interface Campaign {
  id: string;
  name: string;
  channel: 'email' | 'sms';
  status: 'draft' | 'scheduled' | 'sent' | 'sending';
  subject?: string;
  body: string;
  scheduledAt?: string;
  createdAt: string;
}

interface Segment {
  id: string;
  name: string;
  description?: string;
  contactCount?: number;
}

interface Subscriber {
  id: string;
  email: string;
  status: 'active' | 'unsubscribed' | 'pending';
  subscribedAt?: string;
}

const statusColor: Record<Campaign['status'], BadgeColor> = {
  draft: 'gray',
  scheduled: 'blue',
  sent: 'green',
  sending: 'yellow',
};

export function MarketingPage() {
  const [tab, setTab] = useState<Tab>('campaigns');
  return (
    <div>
      <PageHeader title="Marketing" subtitle="Reach contacts at the right moment" />
      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'campaigns', label: 'Campaigns' },
          { value: 'segments', label: 'Segments' },
          { value: 'sequences', label: 'Sequences' },
          { value: 'subscribers', label: 'Subscribers' },
        ]}
      />
      {tab === 'campaigns' && <Campaigns />}
      {tab === 'segments' && <Segments />}
      {tab === 'sequences' && <Sequences />}
      {tab === 'subscribers' && <Subscribers />}
    </div>
  );
}

function Campaigns() {
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<{ data: Campaign[] }>('/campaigns').catch(() => ({ data: [] })),
  });

  const items = q.data?.data ?? [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
        <Button onClick={() => setShowCreate(true)}>+ New campaign</Button>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title="No campaigns yet"
            description="Send broadcast email or SMS to your contacts."
            action={<Button onClick={() => setShowCreate(true)}>+ New campaign</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {items.map((c) => (
            <Card key={c.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                    {c.channel.toUpperCase()} · Created {formatDate(c.createdAt)}
                  </div>
                </div>
                <Badge color={statusColor[c.status]}>{c.status}</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showCreate && (
        <CampaignModal
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['campaigns'] });
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}

function CampaignModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post('/campaigns', {
        name,
        channel,
        subject: channel === 'email' ? subject : undefined,
        body,
        status: 'draft',
      }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  const charCount = body.length;
  const smsLimit = channel === 'sms' ? 160 : Infinity;

  return (
    <Modal onClose={onClose} title="New campaign" width={640}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          if (!body) return setError('Body required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Channel">
          <Select
            value={channel}
            onChange={setChannel}
            options={[
              { value: 'email', label: 'Email' },
              { value: 'sms', label: 'SMS' },
            ]}
          />
        </Field>
        {channel === 'email' && (
          <Field label="Subject">
            <Input value={subject} onChange={setSubject} />
          </Field>
        )}
        <Field
          label={channel === 'email' ? 'Email body' : 'Message'}
          hint={channel === 'sms' ? `${charCount}/${smsLimit} characters` : undefined}
        >
          <Textarea value={body} onChange={setBody} rows={channel === 'email' ? 8 : 4} />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Create draft'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Segments() {
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<{ data: Segment[] }>('/segments').catch(() => ({ data: [] })),
  });

  const items = q.data?.data ?? [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
        <Button onClick={() => setShowCreate(true)}>+ New segment</Button>
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title="No segments yet"
            description="Group your contacts by behavior, tags, or custom criteria."
            action={<Button onClick={() => setShowCreate(true)}>+ New segment</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-2)' }}>
          {items.map((s) => (
            <Card key={s.id}>
              <div style={{ fontWeight: 600 }}>{s.name}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {s.contactCount ?? 0} contacts
              </div>
              {s.description && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-2)', marginBottom: 0 }}>
                  {s.description}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
      {showCreate && (
        <SegmentModal
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['segments'] });
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}

function SegmentModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [criterion, setCriterion] = useState<'tag' | 'no_purchase' | 'recent_signup'>('tag');
  const [tagValue, setTagValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post('/segments', {
        name,
        description: description || undefined,
        rules: [{ field: criterion, value: tagValue }],
      }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title="New segment">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name) return setError('Name required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Name *">
          <Input value={name} onChange={setName} autoFocus />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={setDescription} rows={2} />
        </Field>
        <Field label="Rule">
          <Select
            value={criterion}
            onChange={setCriterion}
            options={[
              { value: 'tag', label: 'Has tag' },
              { value: 'no_purchase', label: 'Never purchased' },
              { value: 'recent_signup', label: 'Recently signed up' },
            ]}
          />
        </Field>
        {criterion === 'tag' && (
          <Field label="Tag name">
            <Input value={tagValue} onChange={setTagValue} placeholder="vip, lead, returning…" />
          </Field>
        )}
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Sequences() {
  return (
    <Card>
      <EmptyState
        title="Sequences"
        description="Multi-step drip campaigns. Build one from the Workflows builder using the 'Send email' / 'Send SMS' / 'Wait' steps."
      />
    </Card>
  );
}

function Subscribers() {
  const q = useQuery({
    queryKey: ['subscribers'],
    queryFn: () => api.get<{ data: Subscriber[] }>('/newsletter/subscribers').catch(() => ({ data: [] })),
  });

  const subs = q.data?.data ?? [];

  if (q.isLoading) return <Spinner />;
  if (subs.length === 0) {
    return (
      <Card>
        <EmptyState title="No subscribers yet" description="Subscribers come in via your newsletter sign-up form." />
      </Card>
    );
  }
  return (
    <Card style={{ padding: 0 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--color-surface-secondary)', textAlign: 'left' }}>
            <th style={cellStyle}>Email</th>
            <th style={cellStyle}>Status</th>
            <th style={cellStyle}>Subscribed</th>
          </tr>
        </thead>
        <tbody>
          {subs.map((s) => (
            <tr key={s.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
              <td style={cellStyle}>{s.email}</td>
              <td style={cellStyle}>
                <Badge color={s.status === 'active' ? 'green' : 'gray'}>{s.status}</Badge>
              </td>
              <td style={cellStyle}>{s.subscribedAt ? formatDate(s.subscribedAt) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const cellStyle: React.CSSProperties = {
  padding: 'var(--space-3)',
  fontSize: 'var(--text-sm)',
};
